"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Plane, Clock, MapPin, ShieldCheck, Users, Briefcase, ChevronRight, PhoneCall, Sparkles, Wifi, Coffee, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const servicesData = [{
  id: "airport-transfers",
  title: "Airport transfers (O'Hare & Midway)",
  category: "FLIGHT LOGISTICS",
  summary: "Reliable pickup and drop-off with automatic flight tracking, inside meet-and-greet, and 60 minutes of complimentary wait time.",
  capacity: "1–6 Passengers",
  luggage: "Up to 5 Large Bags",
  pricingNote: "Flat rates from $95",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/airport_transfer_chauffeur.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/airport_transfer_chauffeur.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/airport_transfer_chauffeur.webp-wvc-sizes",
  altText: "Chauffeur waiting at airport terminal departure curb in Chicago",
  feature1: "Real-time ORD & MDW flight tracking",
  feature2: "Baggage assistance & curbside staging",
  feature3: "60-minute grace period on arrivals",
  icon: "Plane"
}, {
  id: "executive-sedans",
  title: "Executive sedan hire",
  category: "CORPORATE TRAVEL",
  summary: "Pristine black sedans for business meetings, discreet executive transit, and daily point-to-point travel across greater Chicago.",
  capacity: "Up to 3 Passengers",
  luggage: "3 Medium Bags",
  pricingNote: "Hourly or flat rate",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_sedan_interior.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_sedan_interior.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_sedan_interior.png-wvc-sizes",
  altText: "Executive luxury black sedan parked in downtown Chicago at night",
  feature1: "Acoustic privacy glass & climate control",
  feature2: "Onboard device charging & Wi-Fi",
  feature3: "Punctual, commercially licensed drivers",
  icon: "Briefcase"
}, {
  id: "first-class-suvs",
  title: "First-class luxury SUVs",
  category: "GROUP & FAMILY",
  summary: "Spacious Cadillac Escalade and Suburban vehicles providing superior luggage room, extra legroom, and refined all-weather stability.",
  capacity: "Up to 6 Passengers",
  luggage: "6 Full-Size Bags",
  pricingNote: "Premium group comfort",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/luxury_suv_fleet.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/luxury_suv_fleet.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/luxury_suv_fleet.png-wvc-sizes",
  altText: "Black luxury SUV fleet vehicle ready for private executive pickup",
  feature1: "Triple-zone rear cabin climate controls",
  feature2: "Extra room for bulky luggage & golf bags",
  feature3: "Smooth handling in all Midwest seasons",
  icon: "Users"
}, {
  id: "hourly-charter",
  title: "Hourly as-directed charter",
  category: "FLEXIBLE ITINERARY",
  summary: "Keep a dedicated chauffeur and vehicle at your complete disposal for multi-stop client roadshows, dinner galas, and private events.",
  capacity: "Flexible Fleet",
  luggage: "Flexible Capacity",
  pricingNote: "2-hour minimum",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/hourly_chauffeur_service.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/hourly_chauffeur_service.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/hourly_chauffeur_service.webp-wvc-sizes",
  altText: "Professional private chauffeur opening door for client in city center",
  feature1: "Unlimited stops & standby availability",
  feature2: "Direct phone dispatch coordination",
  feature3: "No surge pricing during high-demand hours",
  icon: "Clock"
}, {
  id: "long-distance-trips",
  title: "Long-distance interstate travel",
  category: "REGIONAL TRANSIT",
  summary: "Direct, uninterrupted door-to-door transit between Chicago and neighboring states including Wisconsin, Indiana, Michigan, and Iowa.",
  capacity: "1–6 Passengers",
  luggage: "Full Capacity Available",
  pricingNote: "Guaranteed flat route rates",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/long_distance_limo.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/long_distance_limo.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/long_distance_limo.png-wvc-sizes",
  altText: "Luxury executive car traveling on open highway during twilight",
  feature1: "Door-to-door transit with zero layovers",
  feature2: "Quiet mobile workspace with refreshments",
  feature3: "Coverage across IL, IN, WI, MI & IA",
  icon: "MapPin"
}];
const standardsData = [{
  id: "std-1",
  label: "Fixed flat-rate pricing with zero surge fees"
}, {
  id: "std-2",
  label: "City of Chicago licensed & commercially insured"
}, {
  id: "std-3",
  label: "Complimentary bottled water and phone chargers"
}, {
  id: "std-4",
  label: "24/7 direct telephone dispatch and booking help"
}];
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "Plane":
        return <Plane className="size-5 text-primary" />;
      case "Briefcase":
        return <Briefcase className="size-5 text-primary" />;
      case "Users":
        return <Users className="size-5 text-primary" />;
      case "Clock":
        return <Clock className="size-5 text-primary" />;
      case "MapPin":
        return <MapPin className="size-5 text-primary" />;
      default:
        return <Sparkles className="size-5 text-primary" />;
    }
  };
  return <section id="service-list" data-nav="dark" className="dark bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      {/* Subtle ambient gradient highlight behind top header */}
      <div aria-hidden="true" className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-primary/5 blur-3xl rounded-full" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/40 border border-primary/30 text-accent-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <ShieldCheck className="size-3.5 text-primary" />
            <span>Chicago Executive Transportation</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
            Chauffeur services tailored to your schedule.
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Whether you need a curbside airport pickup, an all-day executive car,
            or long-distance travel, ride in quiet comfort with transparent flat
            rates and professional chauffeurs.
          </p>
        </div>

        {/* Services Showcase Cards */}
        <div className="space-y-8 md:space-y-12">
          {servicesData.map((service, index) => {
          const isReversed = index % 2 === 1;
          return <motion.div key={service.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 24
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.15
          }} transition={{
            duration: 0.5,
            delay: index * 0.08,
            ease: "easeOut"
          }}>
                <Card className="overflow-hidden border border-border bg-card/80 backdrop-blur-xs rounded-xl shadow-lg transition-[border-color,box-shadow] duration-300 hover:border-primary/50 hover:shadow-2xl">
                  <div className={`grid grid-cols-1 lg:grid-cols-12 gap-0 ${isReversed ? "lg:flex-row-reverse" : ""}`}>
                    {/* Visual Media Column */}
                    <div className={`relative min-h-[260px] sm:min-h-[320px] lg:min-h-full lg:col-span-5 overflow-hidden ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
                      <img src={service.imageSrc} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.altText} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transform transition-transform duration-700 ease-out hover:scale-105" />
                      {/* Ambient corner overlay for depth */}
                      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-background/80 pointer-events-none" />

                      {/* Category Badge Floating over image */}
                      <div className="absolute top-4 left-4 z-10">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-sm bg-background/90 backdrop-blur-md border border-border text-xs font-semibold tracking-wider text-foreground">
                          {renderIcon(service.icon)}
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {service.category}
                          </span>
                        </span>
                      </div>
                    </div>

                    {/* Content Column */}
                    <div className={`p-6 sm:p-8 lg:p-10 lg:col-span-7 flex flex-col justify-between ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
                      <div>
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                          <h3 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">
                            {service.title}
                          </h3>
                          <span className="font-mono text-xs sm:text-sm font-semibold px-2.5 py-1 rounded bg-secondary text-secondary-foreground border border-border">
                            {service.pricingNote}
                          </span>
                        </div>

                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                          {service.summary}
                        </p>

                        {/* Capacity & Luggage Strip */}
                        <div className="grid grid-cols-2 gap-3 py-3 px-4 rounded-lg bg-muted/40 border border-border/60 mb-6">
                          <div className="flex items-center gap-2">
                            <Users className="size-4 text-primary shrink-0" />
                            <div className="text-xs sm:text-sm">
                              <span className="text-muted-foreground block text-[11px] uppercase tracking-wide">
                                Seating
                              </span>
                              <span className="font-medium text-foreground">
                                {service.capacity}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Briefcase className="size-4 text-primary shrink-0" />
                            <div className="text-xs sm:text-sm">
                              <span className="text-muted-foreground block text-[11px] uppercase tracking-wide">
                                Luggage
                              </span>
                              <span className="font-medium text-foreground">
                                {service.luggage}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Structured Feature Bullets */}
                        <div className="space-y-2.5 mb-8">
                          <div className="flex items-start gap-2.5">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span className="text-xs sm:text-sm text-foreground/90">
                              {service.feature1}
                            </span>
                          </div>
                          <div className="flex items-start gap-2.5">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span className="text-xs sm:text-sm text-foreground/90">
                              {service.feature2}
                            </span>
                          </div>
                          <div className="flex items-start gap-2.5">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span className="text-xs sm:text-sm text-foreground/90">
                              {service.feature3}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Action Footer */}
                      <div className="pt-4 border-t border-border/70 flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Coffee className="size-3.5 text-primary" />
                          <span>Chilled bottled water included</span>
                        </div>

                        <div className="flex items-center gap-3 w-full sm:w-auto">
                          <Button asChild variant="default" className="w-full sm:w-auto bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors">
                            <Link to="/OrderPage">
                              <span>Book this service</span>
                              <ChevronRight className="size-4" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Global Standard Guarantee Bar */}
        <div className="mt-16 sm:mt-20 p-6 sm:p-8 rounded-xl bg-card border border-border">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-5">
              <h4 className="text-lg sm:text-xl font-bold text-foreground mb-2">
                Every reservation includes our signature standard
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground">
                SANTA LIMO delivers predictable, executive-grade service for every
                mile driven in Chicago and across state lines.
              </p>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {standardsData.map((std, idx) => <div key={std.id} data-index={idx} className="flex items-center gap-2.5 p-3 rounded-lg bg-muted/30 border border-border/50 text-xs sm:text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{std.label}</span>
                </div>)}
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-border/60 flex flex-wrap items-center justify-between gap-4">
            <div className="text-xs text-muted-foreground">
              Questions about custom itineraries or multi-car bookings?
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:3125550100" className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-foreground hover:text-primary transition-colors">
                <PhoneCall className="size-4 text-primary" />
                <span>Call (312) 555-0100</span>
              </a>
              <Button asChild variant="outline" size="sm">
                <Link to="/ContactUsPage">Contact dispatch</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}